<template>
  <div class="flex flex-col gap-6 px-4 h-full md:px-14">
    <div class="flex flex-col md:flex-row items-center justify-between gap-4">
      <TitlePrimary :title="$t('bill.title')" class="w-full md:w-auto flex-[2]" />
      <TitlePrimary class="w-full md:w-auto flex-[5] px-6 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]"
        bgColor="bg-[#00000]" textColor="text-[#7CADB9]"
        :title="$t('bill.form.subtitle')" />
    </div>
    
    <TitlePrimary 
      :title="$t('bill.form.sectionTitle')" 
      bg-color="bg-[#00000]" 
      textColor="text-[#25B0D7]"
      class="border-none shadow-none" 
    />

    <div>
      <!-- Sección de datos -->
      <div class="flex flex-col gap-4 md:flex-row">
        <div class="flex flex-col gap-4 w-full md:flex-1">
          <div class="relative">
            <InputPrimary
              v-model="searchCupText"
              :label="$t('bill.form.labels.cups')"
              type="text"
              color="#2AB4D9"
              required
            />
            <div
              v-if="searchCupResults && searchCupResults.data && searchCupResults.data.length > 0"
              class="absolute left-0 right-0 top-full mt-1 border rounded p-2 bg-white z-20 max-h-48 overflow-y-auto"
              @scroll="handleCupResultsScroll"
            >
              <ul>
                <li v-for="item in searchCupResults.data" :key="item.id" class="hover:bg-gray-100 cursor-pointer"
                  @click="selectCupSearchResult(item)">
                  <p>{{ item.cups }}</p>
                </li>
              </ul>
              <p v-if="isLoadingCupResults" class="text-sm text-[#385660] py-1">{{ $t('common.loading') }}</p>
            </div>
            <div
              v-else-if="searchCupText && searchCupResults && !searchCupResults.data.length"
              class="absolute left-0 right-0 top-full mt-1 border rounded p-2 bg-white z-20"
            >
              <p>{{ $t('bill.form.noResults') }}</p>
            </div>
          </div>
          <p v-if="errors.supply" class="text-red-500 text-sm mt-1">{{ errors.supply }}</p>
          
          <InputPrimary
            v-model="formData.invoice_number"
            :label="$t('bill.fields.number') + ':'"
            type="text"
            color="#2AB4D9"
            required
          />
          <p v-if="errors.invoiceNumber || errors.invoiceNumberInvalid" class="text-red-500 text-sm mt-1">{{ errors.invoiceNumberInvalid || errors.invoiceNumber }}</p>
          
          <InputPrimary
            v-model="formData.invoice_date"
            :label="$t('bill.fields.date') + ':'"
            type="date"
            color="#2AB4D9"
            required
          />
          <p v-if="errors.invoiceDate" class="text-red-500 text-sm mt-1">{{ errors.invoiceDate }}</p>
        </div>

        <div class="flex flex-col gap-4 w-full md:flex-1">
          <InputPrimary
            v-model="formData.total"
            :label="$t('bill.fields.total') + ':'"
            type="number"
            step="0.01"
            color="#2AB4D9"
            required
          />
          <p v-if="errors.total || errors.totalInvalid" class="text-red-500 text-sm mt-1">{{ errors.totalInvalid || errors.total }}</p>
          
          <TitlePrimary 
            :title="$t('bill.fields.document') + ':'" 
            bg-color="bg-[#00000]" 
            textColor="text-[#385660]"
            class="border-none shadow-none" 
          />
          
          <input
            type="file"
            ref="fileInput"
            @change="handleFileChange"
            class="hidden"
            :accept="allowedFileAccept"
          />
          
          <ButtonWhite
            @click="$refs.fileInput.click()"
            class="w-full"
            :title="$t('bill.buttons.upload')"
            bgColor="bg-[#25B0D7]"
            textColor="text-[#fff]"
            hoverBgColor="hover:bg-[#5F99AE]"
          />
          
          <span v-if="formData.file" class="text-[#385660] text-sm">{{ formData.file.name }}</span>
          <p v-if="errors.file" class="text-red-500 text-sm mt-1">{{ errors.file }}</p>
        </div>

        <div class="flex flex-col gap-4 w-full md:flex-1">
          <label for="comments" class="cursor-pointer text-[#385660]">
            {{ $t('bill.form.labels.comments') + ':' }}<span class="ml-0.5">*</span>
          </label>
          <textarea
            v-model="formData.comments"
            id="comments"
            class="text-[#7CADB9] border border-[#3A5762] rounded-xl h-49 p-2 resize-none w-full outline-none shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]"
            required
          ></textarea>
          <p v-if="errors.comments" class="text-red-500 text-sm mt-1">{{ errors.comments }}</p>
        </div>
      </div>
    </div>
    
    <div class="flex items-end justify-end py-8">
      <ButtonWhite
        @click="handleSubmit"
        :title="$t('bill.buttons.save')"
        class="w-1/4"
        bgColor="bg-[#25B0D7]"
        textColor="text-[#fff]"
        hoverBgColor="hover:bg-[#5F99AE]"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';

import ButtonWhite from '@/components/button/ButtonWhite.vue';
import InputPrimary from '@/components/inputs/input-primary.vue';
import TitlePrimary from '@/components/title/title-primary.vue';
import { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const router = useRouter();


const formData = ref({
  invoice_date: '',
  invoice_number: '',
  comments: '',
  supply_id: null,
  total: '',
  file: null,
  cups: '',
  type: 'light' // Por defecto luz
});

// Errores de validación por campo (se muestran inline)
const errors = ref({
  supply: '',
  invoiceDate: '',
  invoiceNumber: '',
  invoiceNumberInvalid: '',
  total: '',
  totalInvalid: '',
  file: '',
  comments: '',
  cups: ''
});

const searchCupText = ref('');
const searchCupResults = ref(null);
const CUPS_PER_PAGE = 20;
const currentCupPage = ref(1);
const hasMoreCupResults = ref(false);
const isLoadingCupResults = ref(false);
const currentCupQuery = ref('');
const isSelectingCupResult = ref(false);
const allowedFileExtensions = ['pdf', 'jpg', 'jpeg', 'png'];
const allowedFileTypes = ['application/pdf', 'image/jpeg', 'image/png'];
const allowedFileAccept = allowedFileExtensions.map(extension => `.${extension}`).join(',');

const isAllowedFile = (file) => {
  if (!file) return true;

  const extension = file.name.split('.').pop()?.toLowerCase();
  const hasAllowedExtension = allowedFileExtensions.includes(extension);
  const hasAllowedType = !file.type || allowedFileTypes.includes(file.type);

  return hasAllowedExtension && hasAllowedType;
};

const clearSelectedFile = (input) => {
  if (input) {
    input.value = '';
  }

  formData.value.file = null;
};

// Función de búsqueda para CUP
const performCupSearch = async (query, append = false) => {

  if (!query || query.length < 2) {
    searchCupResults.value = null;
    currentCupQuery.value = '';
    currentCupPage.value = 1;
    hasMoreCupResults.value = false;
    return;
  }

  if (isLoadingCupResults.value) return;

  if (!append) {
    currentCupPage.value = 1;
    hasMoreCupResults.value = false;
    searchCupResults.value = { data: [] };
    currentCupQuery.value = query;
  }

  try {
    isLoadingCupResults.value = true;
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/supply/user/search`,
      {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          query: query,
          page: currentCupPage.value,
          per_page: CUPS_PER_PAGE,
          search_by: 'cups',
          type: 'both'
          // type: formData.value.type
        }
      }
    );

    if (response.data.value?.data) {
      const filteredResults = response.data.value.data.filter(item =>
        item.cups.toLowerCase().includes(query.toLowerCase())
      );

      const newData = append
        ? [...(searchCupResults.value?.data || []), ...filteredResults]
        : filteredResults;

      const uniqueData = newData.filter(
        (item, index, self) => index === self.findIndex((entry) => entry.id === item.id)
      );

      searchCupResults.value = { data: uniqueData };

      const currentPageFromApi = response.data.value?.current_page ?? currentCupPage.value;
      const lastPageFromApi = response.data.value?.last_page;

      if (lastPageFromApi) {
        hasMoreCupResults.value = currentPageFromApi < lastPageFromApi;
      } else {
        hasMoreCupResults.value = filteredResults.length === CUPS_PER_PAGE;
      }

      if (hasMoreCupResults.value) {
        currentCupPage.value += 1;
      }
    } else {
      searchCupResults.value = append ? searchCupResults.value : null;
      hasMoreCupResults.value = false;
    }
  } catch (error) {
    console.error('Error en la búsqueda de CUP:', error);
    console.error('Detalles del error:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    if (!append) {
      searchCupResults.value = null;
    }
  } finally {
    isLoadingCupResults.value = false;
  }
};

const handleCupResultsScroll = (event) => {
  const target = event.target;
  const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 16;

  if (!isNearBottom || !hasMoreCupResults.value || isLoadingCupResults.value) {
    return;
  }

  performCupSearch(currentCupQuery.value, true);
};

// Watch para ejecutar la búsqueda de CUP
watch(searchCupText, (newSearchText) => {
  if (isSelectingCupResult.value) {
    isSelectingCupResult.value = false;
    return;
  }

  if (newSearchText && newSearchText.length >= 2) {
    performCupSearch(newSearchText, false);
  } else {
    searchCupResults.value = null;
    currentCupQuery.value = '';
    currentCupPage.value = 1;
    hasMoreCupResults.value = false;
  }
});

// Función para seleccionar un resultado de la búsqueda de CUP
const selectCupSearchResult = (result) => {
  isSelectingCupResult.value = true;
  formData.value.cups = result.cups;
  formData.value.supply_id = result.id;
  searchCupText.value = result.cups;
  searchCupResults.value = null;
};

const handleFileChange = (event) => {
  const file = event.target.files[0];

  if (!file) {
    clearSelectedFile(event.target);
    return;
  }

  if (!isAllowedFile(file)) {
    errors.value.file = t('bill.errors.fileType');
    toast.error(t('bill.errors.fileType'));
    clearSelectedFile(event.target);
    return;
  }

  // Validar tamaño del archivo (5MB máximo)
  const maxSize = 5 * 1024 * 1024; // 5MB en bytes
  if (file.size > maxSize) {
    errors.value.file = t('bill.errors.fileSize') + ' (5MB)';
    clearSelectedFile(event.target);
    return;
  }

  // Limpiar posible error previo
  errors.value.file = '';
  formData.value.file = file;
};

const handleSubmit = async () => {
  try {
    // Limpiar errores previos
    Object.keys(errors.value).forEach(k => { errors.value[k] = ''; });

    // Validaciones previas
    let valid = true;
    if (!formData.value.supply_id) {
      errors.value.supply = t('bill.errors.supply');
      valid = false;
    }
    if (!formData.value.invoice_date) {
      errors.value.invoiceDate = t('bill.errors.invoiceDate');
      valid = false;
    }
    if (!formData.value.invoice_number) {
      errors.value.invoiceNumber = t('bill.errors.invoiceNumber');
      valid = false;
    } else if (isNaN(parseInt(formData.value.invoice_number))) {
      errors.value.invoiceNumberInvalid = t('bill.errors.invoiceNumberInvalid');
      valid = false;
    }
    if (!formData.value.total) {
      errors.value.total = t('bill.errors.total');
      valid = false;
    } else if (isNaN(parseFloat(formData.value.total))) {
      errors.value.totalInvalid = t('bill.errors.totalInvalid');
      valid = false;
    }
    if (!formData.value.comments || !formData.value.comments.toString().trim()) {
      errors.value.comments = t('bill.errors.comments') || t('form.validation.comments_required') || 'El comentario es obligatorio';
      valid = false;
    }

    // Validar tamaño del archivo antes de enviar
    const maxSize = 5 * 1024 * 1024; // 5MB en bytes
    if (formData.value.file) {
      if (!isAllowedFile(formData.value.file)) {
        errors.value.file = t('bill.errors.fileType');
        valid = false;
      } else if (formData.value.file.size > maxSize) {
        errors.value.file = t('bill.errors.fileSize') + ' (5MB)';
        valid = false;
      }
    }

    if (!valid) {
      toast.error(t('bill.errors.fixFields'));
      return;
    }

    const formDataToSend = new FormData();

    // Convert and append all form data
    Object.keys(formData.value).forEach(key => {
      if (key === 'file' && formData.value[key]) {
        formDataToSend.append(key, formData.value[key], formData.value[key].name);
      } else if (key === 'invoice_date') {
        // Convertir la fecha a formato YYYY-MM-DD HH:mm:ss
        const date = new Date(formData.value[key]);
        const formattedDate = date.toISOString().slice(0, 19).replace('T', ' ');
        formDataToSend.append(key, formattedDate);
      } else if (key === 'invoice_number') {
        // Ensure invoice_number is a valid number
        const invoiceNumber = parseInt(formData.value[key]);
        if (isNaN(invoiceNumber)) {
          throw new Error(t('bill.errors.invoiceNumberInvalid'));
        }
        formDataToSend.append(key, invoiceNumber);
      } else if (key === 'total') {
        // Convert to number
        const total = parseFloat(formData.value[key]);
        if (isNaN(total)) {
          throw new Error(t('bill.errors.totalInvalid'));
        }
        formDataToSend.append(key, total);
      } else if (key === 'supply_id') {
        // Convert to integer
        formDataToSend.append(key, parseInt(formData.value[key]));
      } else if (key !== 'file') {
        formDataToSend.append(key, formData.value[key]);
      }
    });

    const response = await axios.post(
      `${import.meta.env.VITE_API_URL}/invoice/create`,
      formDataToSend,
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        },
        maxContentLength: 5 * 1024 * 1024, // 5MB
        maxBodyLength: 5 * 1024 * 1024 // 5MB
      }
    );

    if (response.data?.value) {
      toast.success(t('bill.success'));
      setTimeout(() => {
        router.push('/supply-bill');
      }, 1500);
    }
  } catch (error) {
    console.error('Error creating invoice:', error);
    if (error.response?.data?.message?.includes('too large')) {
      console.error('El archivo es demasiado grande. El tamaño máximo permitido es 5MB');
      errors.value.file = t('bill.errors.fileSize');
      toast.error(t('bill.errors.fileSize'));
    } else if (error.response?.data?.errors) {
      console.error('Validation errors:', error.response.data.errors);
      Object.entries(error.response.data.errors).forEach(([field, messages]) => {
        const message = Array.isArray(messages) ? messages[0] : messages;

        if (field === 'file') {
          errors.value.file = t('bill.errors.fileType');
        } else if (field in errors.value) {
          errors.value[field] = message;
        }
      });

      toast.error(errors.value.file || error.response.data.message || t('bill.errors.fixFields'));
    } else {
      toast.error(t('bill.errors.create'));
    }
  }
};
</script>
